'use client'

import { useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { motion, useAnimation } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { 
  Brain, 
  Target, 
  TrendingUp, 
  Search,
  Bot,
  Sparkles,
  BarChart3,
  Shield,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface MarketOutlookSectionProps {
  isMobile?: boolean
}

export function MarketOutlookSection({ isMobile = false }: MarketOutlookSectionProps) {

  const controls = useAnimation()
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  useEffect(() => {
    if (inView) {
      controls.start('visible')
    }
  }, [controls, inView])

  const sectionVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  }

  const cardClass = cn(
    "border",
    isMobile 
      ? "bg-slate-800/90 border-slate-700/50" 
      : "backdrop-blur-xl border-white/20 dark:border-white/10 bg-white/5 dark:bg-black/10"
  )

  const cardStyle = isMobile ? {
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
  } : {
    boxShadow: `0 4px 16px 0 rgba(31, 38, 135, 0.08), 0 0 0 1px rgba(255, 255, 255, 0.08) inset`,
  }

  return (
    <motion.section 
      ref={ref}
      className="mb-12"
      variants={sectionVariants}
      initial="hidden"
      animate={controls}
    >
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
          <Bot className="h-4 w-4 text-primary" />
          <span className="text-xs font-semibold text-primary tracking-wide uppercase">Powered by AI Agents</span>
        </div>
        <h3 className="text-3xl font-semibold mb-2 text-primary">Market Intelligence</h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Deploy AI agents on demand to research competitors, forecast awards, and uncover market trends—powered by 
          our proprietary dataset of millions of historical and active government contract awards, combined with the latest online information.
        </p>
      </div>

      {/* Capability Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4 text-center">
            <Search className="h-6 w-6 md:h-8 md:w-8 text-blue-500 mx-auto mb-2" />
            <h4 className="text-sm md:text-base font-bold mb-1">Competitor Analysis</h4>
            <p className="text-[10px] md:text-xs text-muted-foreground">Uncover who&apos;s winning, their pricing, and where gaps exist</p>
          </CardContent>
        </Card>
        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4 text-center">
            <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-emerald-500 mx-auto mb-2" />
            <h4 className="text-sm md:text-base font-bold mb-1">Award Forecasting</h4>
            <p className="text-[10px] md:text-xs text-muted-foreground">Predict contract timelines, values, and recompete windows</p>
          </CardContent>
        </Card>
        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4 text-center">
            <Target className="h-6 w-6 md:h-8 md:w-8 text-amber-500 mx-auto mb-2" />
            <h4 className="text-sm md:text-base font-bold mb-1">Opportunity Planning</h4>
            <p className="text-[10px] md:text-xs text-muted-foreground">Build your pipeline months ahead with AI-driven capture plans</p>
          </CardContent>
        </Card>
        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4 text-center">
            <BarChart3 className="h-6 w-6 md:h-8 md:w-8 text-violet-500 mx-auto mb-2" />
            <h4 className="text-sm md:text-base font-bold mb-1">Spend Analytics</h4>
            <p className="text-[10px] md:text-xs text-muted-foreground">Track agency spending patterns and budget allocations in real time</p>
          </CardContent>
        </Card>
      </div>

      {/* How It Works — Agent-driven intelligence */}
      <div className="grid gap-3 md:gap-4 md:grid-cols-3">
        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Brain className="h-5 w-5 text-primary flex-shrink-0" />
              <h4 className="font-semibold text-primary">Your AI Research Team</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Task AI agents to deep-dive any agency, incumbent, or contract vehicle on your behalf. Get synthesized reports with bid/no-bid recommendations grounded in real award data.
            </p>
          </CardContent>
        </Card>

        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-primary flex-shrink-0" />
              <h4 className="font-semibold text-primary">Proprietary Data Advantage</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Built on our continuously updated dataset of federal, SLED, and grant awards—enriched with AI-generated insights that surface patterns invisible to manual research.
            </p>
          </CardContent>
        </Card>

        <Card className={cardClass} style={cardStyle}>
          <CardContent className="p-3 md:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-5 w-5 text-primary flex-shrink-0" />
              <h4 className="font-semibold text-primary">Competitive Edge on Demand</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Run competitor win-rate analysis, find teaming partners, and benchmark pricing against historical awards—whenever you need it, tailored to your targets.
            </p>
          </CardContent>
        </Card>
      </div>
    </motion.section>
  )
}
